import { useRateManagement } from '@api/useFreight/useFreight'
import type { Ratebook, Surcharge } from '@api/useFreight/types'
import { useQueryClient } from '@tanstack/react-query'
import { useState, useCallback, useRef } from 'react'

/**
 * Custom hook for managing surcharge data and operations
 * @param ratebook The current ratebook
 * @returns Object containing surcharge data, loading state, and operations
 */
export const useSurchargeData = (ratebook: Ratebook) => {
  const [selectedSurcharge, setSelectedSurcharge] = useState<Surcharge | null>(null)
  const [showRateHistory, setShowRateHistory] = useState(false)
  const [showCreateDrawer, setShowCreateDrawer] = useState(false)
  const gridRef = useRef<any>(null)

  const queryClient = useQueryClient()
  const { useSurchargesByRatebookQuery, exportSurcharges, importSurcharges } = useRateManagement()

  // Fetch surcharges data
  const {
    data: surcharges = [],
    isLoading,
    refetch,
  } = useSurchargesByRatebookQuery({
    counterparty_id: ratebook.counterparty_id,
    book_type: ratebook.book_type,
  })

  /**
   * Handle closing the rate history drawer
   */
  const handleCloseRateHistory = useCallback(() => {
    setShowRateHistory(false)
    setSelectedSurcharge(null)
  }, [])

  /**
   * Handle closing the create/edit surcharge drawer
   */
  const handleCloseCreateDrawer = useCallback(() => {
    setShowCreateDrawer(false)
    setSelectedSurcharge(null)
  }, [])

  /**
   * Handle when surcharge rate changes
   */
  const handleRateChange = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['surcharges'] })

    if (selectedSurcharge) {
      const updatedData = await queryClient.getQueryData<Surcharge[]>([
        'surcharges',
        ratebook.counterparty_id,
        ratebook.book_type,
      ])

      const updatedSurcharge = updatedData?.find((s) => s._id === selectedSurcharge._id)

      if (updatedSurcharge) {
        setSelectedSurcharge(updatedSurcharge)
      }
    }
  }, [queryClient, ratebook.counterparty_id, ratebook.book_type, selectedSurcharge])

  /**
   * Open the edit drawer for a surcharge
   */
  const handleEditSurcharge = useCallback((surcharge: Surcharge) => {
    setSelectedSurcharge(surcharge)
    setShowCreateDrawer(true)
  }, [])

  /**
   * Open the rate history drawer for a surcharge
   */
  const handleViewRateHistory = useCallback((surcharge: Surcharge) => {
    setSelectedSurcharge(surcharge)
    setShowRateHistory(true)
  }, [])

  /**
   * Open the create surcharge drawer
   */
  const handleAddSurcharge = useCallback(() => {
    setSelectedSurcharge(null)
    setShowCreateDrawer(true)
  }, [])

  return {
    surcharges,
    isLoading,
    refetch,
    gridRef,
    selectedSurcharge,
    showRateHistory,
    showCreateDrawer,
    handleCloseRateHistory,
    handleCloseCreateDrawer,
    handleRateChange,
    handleEditSurcharge,
    handleViewRateHistory,
    handleAddSurcharge,
    exportSurcharges,
    importSurcharges,
  }
}
